// src/lib/messagePrefill.js
// Derive AI-message form fields from data we already have on the event,
// so the coach doesn't have to re-type opponent / score / date / location
// every time they want to send a recap or reminder.
//
// This is the data layer for Phase 1 of the smart-composer redesign:
// instead of forcing the coach into a 7-field wizard for a Game Recap,
// we pre-fill 5 of those 7 from the game doc and only ask for the
// "highlights" — the one thing we genuinely don't know.
//
// Each derive*() function returns the `details` object that gets passed
// to the `generateMessage` Cloud Function. Field names match the schema
// defined in src/pages/messages-ai/messageTypes.js so the AI prompt
// renders correctly without changes on the server.

import { format } from 'date-fns';

/** Safe Firestore Timestamp → JS Date. */
function toDate(v) {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (typeof v?.seconds === 'number') return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Capitalize "practice" → "Practice" so it matches the wizard's select options. */
function titleCaseEventType(t) {
  const v = String(t || 'game').toLowerCase();
  return v.charAt(0).toUpperCase() + v.slice(1);
}

/** Format a kickoff date the way the AI prompt expects ("Saturday, June 7 at 10am"). */
function formatHumanDateTime(date) {
  if (!date) return '';
  try {
    return format(date, 'EEEE, MMMM d') + ' at ' + format(date, 'h:mma').toLowerCase().replace(':00', '');
  } catch {
    return '';
  }
}

/** Format a location string from event fieldName + fieldAddress. */
function formatLocation(event) {
  if (!event) return '';
  if (event.fieldName && event.fieldAddress) return `${event.fieldName} (${event.fieldAddress})`;
  return event.fieldName || event.fieldAddress || '';
}

/* ------------------------------------------------------------------ */
/* Per-message-type derivations                                       */
/* ------------------------------------------------------------------ */

/**
 * Game Recap — coach announces a game's result + highlights.
 * Auto-fills: opponent, ourScore, theirScore, date, nextEvent
 * Coach still owns: highlights, shoutouts (both important — AI can't
 * know what was actually exciting).
 */
export function deriveGameRecapDetails(event, opts = {}) {
  if (!event) return {};
  const kickoff = toDate(event.date);
  // Score sides — the team's score depends on whether they were home or away.
  const isHome = event.homeAway === 'home';
  const ourScore =
    isHome ? (event.homeScore != null ? String(event.homeScore) : '')
           : (event.awayScore != null ? String(event.awayScore) : '');
  const theirScore =
    isHome ? (event.awayScore != null ? String(event.awayScore) : '')
           : (event.homeScore != null ? String(event.homeScore) : '');

  return {
    opponent:   event.opponent || '',
    ourScore,
    theirScore,
    date:       kickoff ? format(kickoff, 'yyyy-MM-dd') : '',
    highlights: opts.highlights || '', // Coach must provide
    shoutouts:  opts.shoutouts || '',
    nextEvent:  opts.nextEvent || '',  // Could be auto-derived from schedule (Phase 2)
  };
}

/**
 * Reminder — coach nudges parents the day before a practice/game.
 * Auto-fills: eventType, date, time, location
 * Coach still owns: whatToBring, notes (both optional).
 */
export function deriveReminderDetails(event, opts = {}) {
  if (!event) return {};
  const kickoff = toDate(event.date);
  return {
    eventType:   titleCaseEventType(event.eventType || 'game'),
    date:        kickoff ? format(kickoff, 'yyyy-MM-dd') : '',
    time:        kickoff ? format(kickoff, 'HH:mm') : '',
    location:    formatLocation(event),
    whatToBring: opts.whatToBring || '',
    notes:       opts.notes || '',
  };
}

/**
 * Cancellation — coach calls off an event.
 * Auto-fills: eventType, originalDateTime, nextEvent (if known)
 * Coach still owns: reason (dropdown — Weather/Field/etc.)
 */
export function deriveCancellationDetails(event, opts = {}) {
  if (!event) return {};
  const kickoff = toDate(event.date);
  return {
    eventType:        titleCaseEventType(event.eventType || 'game'),
    originalDateTime: formatHumanDateTime(kickoff),
    reason:           opts.reason || '',     // Coach must pick
    nextEvent:        opts.nextEvent || '',  // Optional — Phase 2 auto-detect
  };
}

/**
 * Schedule Change — coach moves an event.
 * Auto-fills: eventType, originalDateTime
 * Coach still owns: newDateTime, newLocation, reason.
 */
export function deriveScheduleChangeDetails(event, opts = {}) {
  if (!event) return {};
  const kickoff = toDate(event.date);
  return {
    eventType:        titleCaseEventType(event.eventType || 'game'),
    originalDateTime: formatHumanDateTime(kickoff),
    newDateTime:      opts.newDateTime || '',
    newLocation:      opts.newLocation || '',
    reason:           opts.reason || '',
  };
}

/** Single dispatcher — pick the right derive function by messageType. */
export function deriveDetailsForType(messageType, event, opts = {}) {
  switch (messageType) {
    case 'Game Recap':      return deriveGameRecapDetails(event, opts);
    case 'Reminder':        return deriveReminderDetails(event, opts);
    case 'Cancellation':    return deriveCancellationDetails(event, opts);
    case 'Schedule Change': return deriveScheduleChangeDetails(event, opts);
    default:                return opts; // unknown type — caller is on its own
  }
}

/**
 * Which fields the COACH still needs to fill in (or at least review)
 * after auto-fill, by message type. Used to drive the "Edit details"
 * expander in the slide-over composer — we hide the fields we already
 * filled in unless the coach explicitly asks to edit them.
 *
 * Returns an array of field name strings (matches messageTypes.js schema).
 */
export function fieldsCoachShouldReview(messageType) {
  switch (messageType) {
    case 'Game Recap':      return ['highlights', 'shoutouts', 'nextEvent'];
    case 'Reminder':        return ['whatToBring', 'notes'];
    case 'Cancellation':    return ['reason', 'nextEvent'];
    case 'Schedule Change': return ['newDateTime', 'newLocation', 'reason'];
    default:                return [];
  }
}

/**
 * Sanity check — does the auto-fill have enough data to call generateMessage
 * without the coach having to fill anything in first? Used to decide whether
 * to show "Generating..." right away or open with the form expanded.
 */
export function hasMinimumDetails(messageType, details = {}) {
  switch (messageType) {
    case 'Game Recap':
      // Score may legitimately be 0 — check for non-empty strings, not truthy.
      return !!(details.opponent && details.ourScore !== '' && details.theirScore !== '' && details.date);
    case 'Reminder':
      return !!(details.eventType && details.date && details.time && details.location);
    case 'Cancellation':
      return !!(details.eventType && details.originalDateTime && details.reason);
    case 'Schedule Change':
      return !!(details.eventType && details.originalDateTime && details.newDateTime);
    default:
      return false;
  }
}
